'use es6';

import PropTypes from 'prop-types';
import { getRealPath } from '../filterQueryFormat/logic/LogicGroup';
import { getAssociationValueFromFilterBranch } from './strategies/ObjectSegStrategyUtils';
import FilterType from './propTypes/FilterType';
import UISelect from 'UIComponents/input/UISelect';
import { List } from 'immutable';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FLINT } from 'HubStyleTokens/colors';
import FilterFamilyHeadingAssociationSelectValueComponent from './FilterFamilyHeadingAssociationSelectValueComponent';
import { USER_DEFINED } from '../filterQueryFormat/associations/AssociationCategory';
import { useCallback } from 'react';
import FilterUserAction from '../filterQueryFormat/FilterUserAction';
import { CHANGED_ASSOCIATION } from '../filterQueryFormat/UsageActionTypes';
import { getTrackingAssociationType } from '../utilities/userActionsTracking';
import { isExcludedAssociation } from './fetcher/adapter/associationDefinitionsAdapter';
import UIAlert from 'UIComponents/alert/UIAlert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const findAssociationOptionByValue = (associationOptions = [], associationValue) => {
  const flattenedOptions = [];
  associationOptions.forEach(option => {
    if (option.options) {
      flattenedOptions.push(...option.options);
    } else {
      flattenedOptions.push(option);
    }
  });
  return flattenedOptions.find(opt => opt.value === associationValue);
};
export const StyledUISelect = styled(UISelect).withConfig({
  displayName: "FilterFamilyHeadingWithAssociations__StyledUISelect",
  componentId: "a42lig-0"
})(["padding:0 !important;"]);
export const StyledDiv = styled.div.withConfig({
  displayName: "FilterFamilyHeadingWithAssociations__StyledDiv",
  componentId: "a42lig-1"
})(["max-width:320px;"]);
const getFilterFamilyHeadingWithAssociation = (associationOptions = [], associationValue) => {
  const flattenedOptions = [];
  associationOptions.forEach(option => {
    if (option.options) {
      flattenedOptions.push(...option.options);
    } else {
      flattenedOptions.push(option);
    }
  });
  const option = flattenedOptions.find(opt => opt.value === associationValue);
  return option ? option.text : associationValue;
};
const FilterFamilyHeadingWithAssociations = props => {
  const {
    conditionPath,
    filterFamily,
    getFilterFamilyObjectName,
    getObjectAssociationOptions,
    isReadOnly,
    onBranchAssociationChange,
    value,
    baseFilterFamily,
    onUserAction
  } = props;
  const baseObjectName = getFilterFamilyObjectName(baseFilterFamily);
  const associatedObjectName = getFilterFamilyObjectName(filterFamily);
  const associationOptions = getObjectAssociationOptions(filterFamily);
  const filterBranch = value.getIn(getRealPath(conditionPath));
  const associationValue = getAssociationValueFromFilterBranch(filterBranch);
  const onAssociationChange = useCallback(evt => {
    const newAssociationValue = evt.target.value;
    onBranchAssociationChange(conditionPath, newAssociationValue);
    if (typeof onUserAction === 'function') {
      const associationOption = findAssociationOptionByValue(associationOptions, newAssociationValue);
      onUserAction(FilterUserAction({
        action: CHANGED_ASSOCIATION,
        filterFamily,
        subAction: getTrackingAssociationType(associationOption)
      }));
    }
  }, [associationOptions, conditionPath, filterFamily, onBranchAssociationChange, onUserAction]);

  // show raw association value initially (e.g. `1--HUBSPOT_DEFINED`)
  let associationLabel = associationValue;

  // if legacy association is selected
  if (filterBranch.associationTypeId && filterBranch.associationCategory && isExcludedAssociation(filterBranch.associationTypeId, filterBranch.associationCategory)) {
    associationLabel = /*#__PURE__*/_jsx(UIAlert, {
      className: "m-top-1",
      type: "danger",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFilters.FilterFamilyGroupHeadingAssociation.badAssociationWarning"
      })
    });
  }
  // if associations are loaded but empty render "Any" association label
  else if (associationOptions && associationOptions.length === 0) {
    associationLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFilters.FilterFamilyGroupHeadingTranslator.CRM_OBJECT_ANY",
      options: {
        entityName: associatedObjectName
      }
    });
  }
  // in read only mode render association label as a text
  else if (associationOptions && associationOptions.length && isReadOnly) {
    const message = associationValue.endsWith(USER_DEFINED) ? 'customerDataFilters.FilterFamilyGroupHeadingAssociation.labeled' : 'customerDataFilters.FilterFamilyGroupHeadingAssociation.primaryOrAny';
    associationLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: message,
      options: {
        associationLabel: getFilterFamilyHeadingWithAssociation(associationOptions, associationValue)
      }
    });
  }
  // in edit mode render association label as a select
  else if (associationOptions && associationOptions.length && !isReadOnly) {
    associationLabel = /*#__PURE__*/_jsx(StyledUISelect, {
      buttonUse: "transparent",
      className: "inline-block",
      menuWidth: 300,
      onChange: onAssociationChange,
      options: associationOptions,
      placement: "bottom right",
      value: associationValue,
      valueComponent: FilterFamilyHeadingAssociationSelectValueComponent
    });
  }
  return /*#__PURE__*/_jsxs("span", {
    className: "display-block p-left-4 p-right-3",
    children: [/*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFilters.FilterFamilyGroupHeadingTranslator.CRM_OBJECT_ASSOCIATED",
        options: {
          baseObjectName,
          entityName: associatedObjectName
        }
      })
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(StyledDiv, {
      className: "association-label",
      children: associationLabel
    }), /*#__PURE__*/_jsx("span", {
      style: {
        color: FLINT
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFilters.FilterFamilyGroupHeadingAssociation.where"
      })
    })]
  });
};
FilterFamilyHeadingWithAssociations.propTypes = {
  baseFilterFamily: PropTypes.string.isRequired,
  conditionPath: PropTypes.instanceOf(List),
  filterFamily: PropTypes.string.isRequired,
  getFilterFamilyObjectName: PropTypes.func.isRequired,
  getObjectAssociationOptions: PropTypes.func,
  isReadOnly: PropTypes.bool.isRequired,
  onBranchAssociationChange: PropTypes.func,
  onUserAction: PropTypes.func,
  value: FilterType.isRequired
};
export default FilterFamilyHeadingWithAssociations;